import { ɵɵCdkFixedSizeVirtualScroll, ɵɵCdkVirtualForOf, ɵɵCdkVirtualScrollViewport } from '@angular/cdk/overlay';
import { AfterViewInit, Component, computed, ElementRef, HostListener, inject, input, OnDestroy, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, MatButtonToggle, MatButtonToggleChange, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatOption, MatSelect, MatSelectChange } from '@angular/material/select';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { Store } from '@ngrx/store';
import { FullscreenLayoutAreas } from '@src/assets/css/fullscreen.template.scss';
import { CardViewType, SearchType } from '@src/suite/common/enums/enums';
import { NavigationDirection } from '@src/suite/common/enums/navigation-direction';
import { FullscreenActions, selectCurrentRecord, SidecarEfffectActions } from '@src/suite/common/store/fullscreen';
import { selectItemLayoutState } from '@src/suite/common/store/fullscreen/selectors/fullscreen.selectors';
import {
    selectSavedAssignmentViewStates,
    selectSidecarAssignments,
    selectSidecarCurrentAssignmentVS,
    selectSidecarSelectedAssignment,
    selectSidecarSelectedAssignmentIndex,
    selectSidecarSelectedAssignmentListSearchType,
    selectSidecarTotalAssignmentCount,
} from '@src/suite/common/store/fullscreen/selectors/sidecar.selectors';
import { selectFieldDefsDict } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N, selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { selectTopicRecordsMap } from '@src/suite/common/store/records/records.selectors';
import { I18nKeys } from '@src/suite/common/utils/i18n.default';
import { getCardViewTypeForWidth } from '@src/suite/common/utils/util';

import { ClickStopPropagationDirective } from '../../directives/stop-propagation';
import { ObjectTypes } from '../../enums/object-types';
import { AssignmentFullScreenDialogComponent } from '../../st-full-screen-dialog/assignment-full-screen/assignment-full-screen';
import { AssignmentComponent } from '../assignments-list/assignment/assignment';
import { StAssignmentCondensedComponent } from '../assignments-list/st-assignment-condensed/st-assignment-condensed';
import { StCondensedListHeaderLabelsComponent } from '../condensed-list-labels/st-condensed-list-header-labels';
import { StItemLayoutToggleComponent } from '../item-layout-toggle/st-item-layout-toggle';
import { SimpleSearchBoxAutocompleteComponent } from '../search-box/simple-search-box-autocomplete/simple-search-box-autocomplete';

@Component({
    selector: 'st-assignment-list-sidecar',
    imports: [
        AssignmentComponent,
        AssignmentFullScreenDialogComponent,
        ClickStopPropagationDirective,
        MatButtonToggle,
        MatButtonToggleGroup,
        MatFormField,
        MatIcon,
        MatIconButton,
        MatLabel,
        MatOption,
        MatSelect,
        ɵɵCdkFixedSizeVirtualScroll,
        ɵɵCdkVirtualForOf,
        ɵɵCdkVirtualScrollViewport,
        SimpleSearchBoxAutocompleteComponent,
        StAssignmentCondensedComponent,
        StCondensedListHeaderLabelsComponent,
        StItemLayoutToggleComponent,
    ],
    templateUrl: './assignment-list-sidecar.html',
    styleUrl: './assignment-list-sidecar.scss',
    providers: [{ provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, useValue: { hideSingleSelectionIndicator: false } }],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-assignment-list-sidecar',
    },
})
export class AssignmentListSidecarDialogComponent implements OnInit, AfterViewInit, OnDestroy {
    store = inject(Store);
    dialogRef = inject(MatDialogRef<AssignmentListSidecarDialogComponent>);
    elmRef = inject(ElementRef);

    SearchType = SearchType;
    NavigationDirection = NavigationDirection;
    CardViewType = CardViewType;
    ObjectTypes = ObjectTypes;
    private containerResizeObserver: ResizeObserver;

    readonly languageSettings = this.store.selectSignal(selectI18N);
    readonly assignmentsLabel = this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebAssignmentsLabel));
    readonly topicItemLayoutState = this.store.selectSignal(selectItemLayoutState);

    public readonly searchTerm = signal('');
    private readonly currentWidth = signal(0);

    readonly selectedListSearchTypeInternal = computed(() => this.store.selectSignal(selectSidecarSelectedAssignmentListSearchType(this.sidecarMenu()))());
    readonly savedViewDropdownVisible = computed(() => this.selectedListSearchType() === SearchType.SAVED);
    readonly selectAssignments = computed(() => this.store.selectSignal(selectSidecarAssignments(this.sidecarMenu()))());
    readonly selectSavedViewStates = computed(() => this.store.selectSignal(selectSavedAssignmentViewStates(this.sidecarMenu()))());
    readonly currentViewState = computed(() => this.store.selectSignal(selectSidecarCurrentAssignmentVS(this.sidecarMenu()))());
    readonly currentSelectedAssignment = computed(() => this.store.selectSignal(selectSidecarSelectedAssignment(this.sidecarMenu()))());
    readonly allTopicsMap = computed(() => this.store.selectSignal(selectTopicRecordsMap)());

    readonly currentSelectedRecord = computed(() => this.store.selectSignal(selectCurrentRecord)());
    readonly totalCount = computed(() => this.store.selectSignal(selectSidecarTotalAssignmentCount(this.sidecarMenu()))());
    readonly indexInParentToShow = computed(() => {
        const indexInParent = this.store.selectSignal(selectSidecarSelectedAssignmentIndex(this.sidecarMenu()))();
        if (indexInParent == null) {
            return 0;
        }
        return indexInParent + 1;
    });

    readonly isSideCarMenuSelected = computed(() => {
        return this.sidecarMenu();
    });
    readonly isAssignmentsCondensedLayout = computed(() => this.topicItemLayoutState().assignmentsSidecar);
    readonly showApproveColumn = computed(() => !!(this.store.selectSignal(selectFieldDefsDict)()?.[PSTFieldNames.PSTApproved] as FieldDef)?.AssignmentProp);

    readonly parentTopicMosActive = computed(() => {
        const topic = this.allTopicsMap()?.get(this.currentSelectedAssignment()?.topicMetadata?.Guid);

        //TODO: there is a chance if the assignment is from assignment list, we don't have the needed topic data
        return topic?.MOSControl;
    });

    readonly selectedListSearchType = computed(() => {
        const selectedListSearchType = this.selectedListSearchTypeInternal();
        if (!selectedListSearchType) {
            return SearchType.SAVED;
        }
        return selectedListSearchType;
    });

    readonly itemSize = computed(() => {
        if (this.isAssignmentsCondensedLayout()) {
            return 50;
        }
        if (this.cardViewType() === CardViewType.SMALL) {
            return 122;
        } else {
            return 138;
        }
    });

    readonly cardViewType = computed(() => getCardViewTypeForWidth(this.currentWidth(), this.isAssignmentsCondensedLayout()));

    readonly sidecarMenu = input<FullscreenLayoutAreas>();

    ngOnInit() {
        if (!this.selectSavedViewStates()) {
            this.store.dispatch(SidecarEfffectActions.loadSidecarAssignmentViewStates({ sidecarType: this.sidecarMenu() }));
        }
    }

    ngAfterViewInit(): void {
        this.containerResizeObserver = new ResizeObserver(entries => {
            const width = entries?.[0]?.contentRect?.width;
            if (width) {
                this.currentWidth.set(width);
            }
        });

        this.containerResizeObserver?.observe(this.elmRef?.nativeElement);
    }

    ngOnDestroy(): void {
        this.containerResizeObserver?.unobserve(this.elmRef?.nativeElement);
        this.containerResizeObserver?.disconnect();
    }

    public async toggleSearchTab(event: MatButtonToggleChange) {
        this.store.dispatch(SidecarEfffectActions.setAssignmentsListSearchType({ searchType: event.value, sidecarType: this.sidecarMenu() }));
        if (this.selectedListSearchType() === SearchType.SEARCH) {
            this.store.dispatch(SidecarEfffectActions.clearSearchAssignment({ sidecarType: this.sidecarMenu() }));
        }
        if (this.savedViewDropdownVisible()) {
            // if we are switching from search to saved views, and there is a saved view selected
            // we need to prefetch again
            this.searchTerm.set('');
            this.store.dispatch(SidecarEfffectActions.reloadSidecarAssignmentBasedOnCurrentViewState({ sidecarType: this.sidecarMenu() }));
        }
    }

    public onSearchHandler(value: string | number) {
        this.store.dispatch(SidecarEfffectActions.searchAssignments({ searchTerm: value.toString(), sidecarType: this.sidecarMenu() }));
    }

    public cancelNew() {
        this.dialogRef.close();
    }

    public trackByGuid(_index, node) {
        if (node) {
            return node?.Guid;
        }
    }

    onViewStateGuidChanged(viewState: MatSelectChange) {
        this.store.dispatch(SidecarEfffectActions.setSidecarAssignmentCurrentViewState({ guid: viewState.value, sidecarType: this.sidecarMenu() }));
    }

    onFullscreenHandler(data: StorytellingRecord) {
        this.store.dispatch(FullscreenActions.openAssignment({ recordGuid: data.Guid, recordType: data.Type, sidecarMenu: this.sidecarMenu(), vsGuid: this.currentViewState()?.Guid }));
    }

    setAssignmentsCondensedLayout(isCondensed: boolean): void {
        this.store.dispatch(FullscreenActions.setItemLayoutState({ state: { assignmentsSidecar: isCondensed } }));
    }

    public navigate(_event, direction: NavigationDirection) {
        this.store.dispatch(FullscreenActions.navigateAssignment({ direction, sidecarMenu: this.sidecarMenu() }));
    }

    @HostListener('document:keyup', ['$event'])
    public navigateForward(event) {
        if (
            event &&
            event.target &&
            event.target.tagName &&
            !(
                event.target.tagName.toLowerCase() === 'input' ||
                event.target.tagName.toLowerCase() === 'textarea' ||
                event.target.classList.contains('fr-element') ||
                event.target.classList.contains('fr-view')
            )
        ) {
            if (event.code === 'Period') {
                this.navigate(event, NavigationDirection.NEXT);
            } else if (event.code === 'Comma') {
                this.navigate(event, NavigationDirection.PREVIOUS);
            }
        }
    }
}
